import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import {
  FormlyFormOptions,
  FormlyFieldConfig,
  FormlyForm,
} from "@ngx-formly/core";
import { Observable, Subscription } from "rxjs";
import {
  QuestionType,
  Survey,
  SurveyQuestion,
  SurveySection,
  SortChangedEvent,
  SurveySkipCondition,
  SurveyAnswerModel,
  SaveResponse,
} from "../../models/survey";
import { WorkflowStatusChange } from "../../models/workflow";
import { UntilDestroy } from "@ngneat/until-destroy";

@UntilDestroy({ checkProperties: true, arrayName: "subscriptions" })
@Component({
    selector: "app-survey",
    templateUrl: "./survey.component.html",
    styleUrls: ["./survey.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SurveyComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isHeaderVisible = true;
  @Input() isDisabled = false;
  @Input() isEdit: boolean;
  @Input() ignoreSkips: boolean;
  @Input() survey: Survey;
  @Input() minSortId: number;
  @Input() maxSortId: number;
  @Input() questionTypes: QuestionType[];
  @Input() lastEdit: Date;
  @Input() modifyQuestion$: Observable<SaveResponse>;
  @Input() workflowStatusChanges: WorkflowStatusChange[] = [];
  @Input() disableQuestionReview = false;
  @Output() onEditSectionClicked = new EventEmitter<SurveySection>();
  @Output() onDeleteSectionClicked = new EventEmitter<SurveySection>();
  @Output() onSectionSortChanged = new EventEmitter<SortChangedEvent>();
  @Output() onAddQuestionClicked = new EventEmitter<SurveySection>();
  @Output() onAddSectionClicked = new EventEmitter<void>();
  @Output() onEditQuestionClicked = new EventEmitter<SurveyQuestion>();
  @Output() onDeleteQuestionClicked = new EventEmitter<SurveyQuestion>();
  @Output() onQuestionSortChanged = new EventEmitter<SortChangedEvent>();
  @Output() onSaveResponse = new EventEmitter<SurveyAnswerModel>();
  @Output() onSurveyIsValidChanged = new EventEmitter<boolean>();

  public form = new FormGroup({});
  public options: FormlyFormOptions = {};
  public fields: FormlyFieldConfig[] = [];
  public hiddenQuestionIds: string[] = [];
  // private route: ActivatedRoute;

  get formIsValid() {
    return this.form.valid;
  }

  subscription: Subscription;
  subscriptions: Subscription[] = [];
  sectionsCollapsed = false;
  languageId = "en";
  surveyIsValid = true;
  surveyIsDisabled = false;
  indicatorType = 0;
  hasWorkflow = false;
  administrativeUnitSurveyId = "";

  // Moved showWorkflow evaluation to ngOnInit to avoid
  // TS initialization error.
  showWorkflow = false;

  @ViewChild(FormlyForm, { static: true }) set formlyForm(
    formlyForm: FormlyForm
  ) {}

  constructor(private route: ActivatedRoute) {}

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.lastEdit && !changes.lastEdit.firstChange) ||
      (changes.survey && !changes.survey.firstChange)
    ) {
      this.renderSurvey();
    }
  }

  ngOnInit(): void {
    // Only show workflow if not one of these custom Indicators
    this.showWorkflow =
      this.route.snapshot.data["indicatorType"] !== 8 &&
      this.route.snapshot.data["indicatorType"] !== 14 &&
      this.route.snapshot.data["indicatorType"] !== 34;

    this.subscription = this.modifyQuestion$?.subscribe({
      next: (response: SaveResponse) => {
        this.fields.forEach((section) => {
          section.fieldGroup.forEach((question) => {
            if (question.key === response.questionId) {
              question.props.question.saving = false;
              question.props.question.saved = true;
              question.props.trigger.update(v => "saved");
            }
          });
        });
      },
    });

    this.administrativeUnitSurveyId = this.route.snapshot.paramMap.get("administrativeUnitSurveyId");

    this.generateAnswerLookup();
    this.renderSurvey();
    this.onSurveyIsValidChanged.emit(this.form.valid);
    this.subscriptions.push(this.form.valueChanges.subscribe(() =>
      this.onSurveyIsValidChanged.emit(this.form.valid)
    ));

    //we only pass this in when access through the Survey Manager, the rest of the time, the available workflow btns are pulled through the survey-workflow-bar component
    this.hasWorkflow = (this.survey.workflowStatusChanges as unknown[])?.length > 0;
  }

  public isTextQuestion(questionTypeId: string) {
    const type = this.getQuestionType(questionTypeId);
    return type === "surveytextarea" || type === "surveyinput";
  }

  public renderSurvey() {
    this.fields = this.renderSections(this.survey);
  }

  public generateAnswerLookup() {
    this.survey.answers.forEach((resp) => {
      this.survey[resp.questionId] = resp.answer;
      this.survey[resp.questionId + "-comments"] = resp.comments;
    });
  }

  public getQuestionsToClear() {
    const questionsToClear: string[] = [];
    this.survey.sections.forEach((section) => {
      if (this.shouldHideItem(section)) {
        section.questions.forEach((question) => {
          questionsToClear.push(question.id);
        });
      } else {
        section.questions.forEach((question) => {
          if (this.shouldHideItem(question)) {
            questionsToClear.push(question.id);
          }
        });
      }
    });

    return questionsToClear;
  }

  public onWorkflowButtonClicked() {
    this.renderSurvey();
  }

  private renderSections(survey: Survey): FormlyFieldConfig[] {
    const fieldConfig: FormlyFieldConfig[] = [];
    survey.sections.sort((a, b) => a.sortId - b.sortId);

    survey.sections.forEach((section: SurveySection) => {
      //const hideSection = this.shouldHideItem(section); // 10/12/2022 - BTH - prevent re-rendering form in favor of letting hideExpression do the work

      fieldConfig.push({
        wrappers: ["page"],
        // 10/12/2022 - BTH - prevent re-rendering form in favor of letting hideExpression do the work
        hideExpression: (survey) => this.shouldHideItem(section),
        //hideExpression: hideSection,
        templateOptions: {
          language: this.survey.language,
          section: section,
          minSortId: this.minSortId,
          maxSortId: this.maxSortId,
          isEdit: this.isEdit,
          onSectionOrderChanged: ($event: SortChangedEvent) =>
            this.sectionOrderChanged($event),
          onEditSectionClicked: ($event: SurveySection) =>
            this.editSectionClicked($event),
          onAddQuestionClicked: ($event: SurveySection) =>
            this.addQuestionClicked($event),
          onDeleteSectionClicked: ($event: SurveySection) =>
            this.deleteSectionClicked($event),
        },
        fieldGroup: this.renderQuestions(section.questions),
      });
    });

    return fieldConfig;
  }

  private renderQuestions(questions: SurveyQuestion[]): FormlyFieldConfig[] {
    const fieldConfig: FormlyFieldConfig[] = [];

    if (!questions) {
      // || this.shouldHideItem(section)) { // 10/12/2022 - BTH - prevent re-rendering form in favor of letting hideExpression do the work
      return fieldConfig;
    }

    questions.sort((a, b) => a.sortId - b.sortId);

    const sortIds = questions.map((m) => m.sortId);
    const minSortId = sortIds.length == 0 ? 1 : Math.min(...sortIds);
    const maxSortId = sortIds.length == 0 ? 0 : Math.max(...sortIds);

    questions.forEach((question) => {
      const formlyType = this.getQuestionType(question.questionTypeId);
      const modelOptions = this.getModelOptions(formlyType);

      const shouldHideQuestion = (questionToHide) => {
        return (
          (!this.isEdit && formlyType == "surveyhidden") ||
          this.shouldHideItem(questionToHide)
        );
      };

      // 10/12/2022 - BTH - prevent re-rendering form in favor of letting hideExpression do the work
      // const index = this.hiddenQuestionIds.indexOf(question.id);
      // if (hideQuestion && index == -1) {
      //   this.hiddenQuestionIds.push(question.id);
      // }
      // if (!hideQuestion && index > -1) {
      //   this.hiddenQuestionIds.splice(index, 1);
      // }

      // 20513 - Hide the question review wrapper
      if (this.disableQuestionReview) {
        question.isReviewRequired = false;
      }

      const trig = signal("saving");

      fieldConfig.push({
        key: question.id.toString(),
        type: formlyType,
        hideExpression: () => shouldHideQuestion(question),
        //hideExpression: (survey: Survey) => hideQuestion, // 10/12/2022 - BTH - prevent re-rendering form in favor of letting hideExpression do the work
        modelOptions,
        templateOptions: {
          language: this.survey.language,
          question: question,
          trigger: trig,
          minSortId: minSortId,
          maxSortId: maxSortId,
          required: question.isRequired, // && !hideQuestion, // 10/12/2022 - BTH - prevent re-rendering form in favor of letting hideExpression do the work
          disabled: question.isReadOnly || this.isDisabled,
          isEdit: this.isEdit,
          administrativeUnitSurveyId: this.administrativeUnitSurveyId,
          showSpinner:
            formlyType === "surveytextarea" || formlyType === "surveytext"
              ? false
              : true,
          onQuestionOrderChanged: ($event: SortChangedEvent) =>
            this.questionOrderChanged($event),
          onEditQuestionClicked: ($event: SurveyQuestion) =>
            this.editQuestionClicked($event),
          onDeleteQuestionClicked: ($event: SurveyQuestion) =>
            this.deleteQuestionClicked($event),
          onSubmitAnswerReview: ($event: WorkflowStatusChange[]) =>
          (this.workflowStatusChanges = this.survey.workflowStatusChanges =
            $event),
        },
        hooks: {
          onInit: (field) => {
            if (!this.isEdit) {
              this.subscriptions.push(
                field.formControl.valueChanges.subscribe((value) => {
                //field.options.fieldChanges.subscribe((event) => {
                  this.fields.forEach((s) => { //sections
                    s.fieldGroup.forEach((q) => { //questions
                      if (q.key === field.key) {
                        if (!this.isTextQuestion(question.questionTypeId)) {
                          q.props.saving = true;
                          question.saving = true;
                          q.props.trigger.update(v => "saving");
                        } else {
                          q.props.saving = false;
                          question.saving = false;
                        }
                      }
                    });
                  });

                  const responseToSaveToDb = {
                    administrativeUnitSurveyId: null,
                    questionId: field.key,
                    comments: this.survey[field.key + "-comments"],
                    answer: this.survey[field.key.toString()],
                  } as SurveyAnswerModel;
          
                  this.onSaveResponse.emit(responseToSaveToDb);
                })
              );
            }

          },
        },
      });
    });
    return fieldConfig;
  }

  private getModelOptions(questionType: string) {
    const updateOnBlur = {
      updateOn: "blur",
    } as FormlyFieldConfig["modelOptions"];

    const questionsToUpdateOnBlur = [
      "surveyinput",
      "surveytextarea",
      "surveydate",
    ];

    return questionsToUpdateOnBlur.includes(questionType) ? updateOnBlur : null;
  }

  private shouldHideItem(item: any): boolean {
    if (this.ignoreSkips || !item.conditions || item.conditions.length == 0) {
      return false;
    }

    let shouldHide = false;

    switch (item.conditionsOperator.toLowerCase()) {
      case "or": // Any true condition will hide
        shouldHide = item.conditions.some((condition) => {
          return this.isConditionMet(condition);
        });
        break;
      case "and": // All conditions must be true to hide
        shouldHide = item.conditions.every((condition) => {
          return this.isConditionMet(condition);
        });
        break;
      default:
        // Default to 'ALL'
        shouldHide = item.conditions.every((condition) => {
          return this.isConditionMet(condition);
        });
        break;
    }

    return shouldHide;
  }

  private isConditionMet(condition: SurveySkipCondition): boolean {
    let isTrue = false;
    let re = null;
    const val = this.survey[condition.itemToHideSiblingId];

    switch (condition.operation) {
      case "===":
        isTrue = (val || "") === condition.condition;
        break;
      case "<>":
        isTrue = val !== condition.condition;
        break;
      case "regex":
        re = new RegExp(condition.condition, "gi");
        if (val) {
          isTrue = val.toString().match(re) !== null;
        }
        break;
      default:
        isTrue = val === condition.condition;
        break;
    }

    return condition.negateFlag ? !isTrue : isTrue;
  }

  private getQuestionType(questionTypeId: string): string {
    return this.questionTypes.find((item) => item.id === questionTypeId)
      .formlyComponentKey;
  }

  addSectionClicked() {
    this.onAddSectionClicked.emit();
  }

  editSectionClicked(section: SurveySection) {
    this.onEditSectionClicked.emit(section);
  }

  deleteSectionClicked(section: SurveySection) {
    this.onDeleteSectionClicked.emit(section);
  }

  sectionOrderChanged(evt: SortChangedEvent) {
    this.onSectionSortChanged.emit(evt);
  }

  addQuestionClicked(section: SurveySection) {
    this.onAddQuestionClicked.emit(section);
  }

  editQuestionClicked(question: SurveyQuestion) {
    this.onEditQuestionClicked.emit(question);
  }

  deleteQuestionClicked(question: SurveyQuestion) {
    this.onDeleteQuestionClicked.emit(question);
  }

  questionOrderChanged(evt: SortChangedEvent) {
    this.onQuestionSortChanged.emit(evt);
  }

  private markUnansweredQuestions() {
    this.fields.forEach((page) => {
      page.fieldGroup.forEach((group) => {
        if (
          this.form.controls[group.key.toString()] &&
          this.form.controls[group.key.toString()].invalid
        ) {
          this.survey.sections;
          group.props.question.saved = false;
        }
      });
    });
  }

  onSubmitRequest() {
    //here we need to check that all required questions have been submitted,
    //if not, we need to highlight all the required questions red
    this.markUnansweredQuestions();
  }

  getSection(sectionId: string): SurveySection {
    return this.survey.sections.find((f) => f.id == sectionId);
  }

  getButtonColor() {
    return this.form.valid ? "blue" : "gray";
  }

  setIgnoreSkip(e) {
    this.ignoreSkips = e.checked;
    this.renderSurvey();
  }

  toggleSectionCollapse() {
    this.survey.sections.forEach(
      (f) => (f.isExpanded = this.sectionsCollapsed)
    );
    this.sectionsCollapsed = !this.sectionsCollapsed;
  }
}
